package com.dotdrives.web.pages.nextsteps

import com.dotdrives.design.DotTone
import com.dotdrives.model.DriveHealthEntry
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import com.dotdrives.web.ui.classSuffix
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName

external interface PipelineHealthPanelProps : Props {
    var loading: Boolean
    var entries: List<DriveHealthEntry>
    var error: String?
    var onRetry: () -> Unit
}

/** "Pipeline health" (`portal-next-steps.dc.html`): drives sitting in a phase at least twice
 * as long as the team's historical median for that phase. */
val PipelineHealthPanel = FC<PipelineHealthPanelProps> { props ->
    div {
        className = ClassName("ns-health-card")
        div {
            className = ClassName("ns-health-head")
            div { className = ClassName("ns-health-title"); +"Pipeline health" }
            if (props.entries.isNotEmpty()) {
                DotPill { text = "${props.entries.size} STUCK"; tone = DotTone.Info }
            }
        }
        div { className = ClassName("ns-health-sub"); +"Drives sitting in a phase longer than your team's median." }

        props.error?.let { error ->
            DotBanner { message = error; onRetry = props.onRetry; className = "ns-inline-banner" }
        }

        when {
            props.loading && props.entries.isEmpty() -> div {
                className = ClassName("ns-health-list")
                repeat(2) { DotSkeleton { height = "70px" } }
            }
            props.error != null && props.entries.isEmpty() -> Unit
            props.entries.isEmpty() -> div {
                className = ClassName("ns-health-sub")
                // Nothing stuck is a good outcome with no follow-up action to offer, unlike a
                // content list's "create your first" empty state -- a quiet status line, not
                // DotEmptyState's action-button shape, is the honest fit here.
                +"Nothing stuck right now — every drive is moving at its usual pace."
            }
            else -> div {
                className = ClassName("ns-health-list")
                props.entries.forEach { entry ->
                    HealthRow {
                        key = entry.driveId.unsafeCast<Key>()
                        this.entry = entry
                    }
                }
            }
        }
    }
}

private external interface HealthRowProps : Props {
    var entry: DriveHealthEntry
}

private val HealthRow = FC<HealthRowProps> { props ->
    val entry = props.entry
    val tone = healthTone(entry.daysInPhase, entry.medianDays)
    val pct = healthBarPercent(entry.daysInPhase, entry.medianDays)
    val barStyle = js("({})")
    barStyle.width = "$pct%"

    div {
        className = ClassName("ns-health-item dot-tone-${tone.classSuffix()}")
        div {
            className = ClassName("ns-health-item-head")
            span { className = ClassName("ns-health-item-name"); +entry.driveName }
            span { className = ClassName("ns-health-item-meta"); +healthMetaLabel(entry) }
        }
        div {
            className = ClassName("ns-health-track")
            div { className = ClassName("ns-health-fill"); style = barStyle }
        }
        div {
            className = ClassName("ns-health-suggest")
            // "Add as step" (writing this suggestion into the drive's phase-task template)
            // touches the drives module's own write path -- out of scope here, so this stays
            // informational text rather than a dead-looking control (no `<a href="#">`).
            +"💡 ${entry.suggestion}"
        }
    }
}
